import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Typography } from '@mui/material';
import DocumentView from 'app/shared/components/document/DocumentView';
import PropTypes from 'prop-types';

import {
  getFileDateTimeName,
  getFileNameFromContentDispositionHeader,
  printFileFromBlob,
} from 'utils/file';
import { logger } from 'utils/logger';
import { downloadOrShareBlob } from 'utils/nativeFile';

import { showMessageAction } from 'store/global/actions';

import {
  formGenerationPreviewPlaceholderStyles,
  formGenerationPreviewStyles,
} from '../LoanFormGenerationForm.styles';

const PREVIEW_DOC_ID = 'loan-form-generation-inline-preview';

const LoanFormGenerationPreviewPanel = ({
  generatedFormBlobRef,
  contentDispositionRef,
  generationCount,
  loanNumber,
}) => {
  const dispatch = useDispatch();

  const getDocumentCall = useCallback(
    () => ({ data: generatedFormBlobRef.current }),
    [generatedFormBlobRef],
  );

  const onDownloadDocument = useCallback(
    async e => {
      e.preventDefault();

      try {
        const formName = getFileNameFromContentDispositionHeader({
          contentDisposition: contentDispositionRef.current,
          extension: 'pdf',
        });

        const fileName = getFileDateTimeName({
          fileName: `${formName ?? 'Form'}_${loanNumber}`,
          extension: 'pdf',
        });

        await downloadOrShareBlob(generatedFormBlobRef.current, fileName);
      } catch (err) {
        logger.error(err);

        dispatch(showMessageAction({ message: "Couldn't download document", variant: 'error' }));
      }
    },
    [dispatch, loanNumber, generatedFormBlobRef, contentDispositionRef],
  );

  const onPrintDocument = useCallback(async () => {
    try {
      await printFileFromBlob(generatedFormBlobRef.current);
    } catch (err) {
      logger.error(err);

      dispatch(showMessageAction({ message: 'Could not print document', variant: 'error' }));
    }
  }, [dispatch, generatedFormBlobRef]);

  if (!generationCount) {
    return (
      <Box sx={formGenerationPreviewPlaceholderStyles}>
        <Typography color="text.secondary" variant="regular12">
          Live form preview
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={formGenerationPreviewStyles}>
      <DocumentView
        isDownloadable
        isPrintable
        isRestrictedFunctionality
        isView
        docId={`${PREVIEW_DOC_ID}-${generationCount}`}
        getDocumentCall={getDocumentCall}
        isControlVisible={false}
        onDownloadDocument={onDownloadDocument}
        onPrintDocument={onPrintDocument}
      />
    </Box>
  );
};

LoanFormGenerationPreviewPanel.propTypes = {
  contentDispositionRef: PropTypes.shape({ current: PropTypes.string }),
  generatedFormBlobRef: PropTypes.shape({ current: PropTypes.object }),
  generationCount: PropTypes.number,
  loanNumber: PropTypes.string,
};

export default memo(LoanFormGenerationPreviewPanel);
